import {
  FlexColumn,
  FlexRowCenterVertical,
  StyleUtils,
  TabBar,
  useTabBarTabs,
} from '@main/core-ui';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Button } from '../Button';
import {
  filterEmptyEnvVars,
  formatJsonEnvironmentObject,
  getHydratedCustomFunctionState,
} from './helpers';
import { customFunctionMessages } from './messages';
import {
  CustomFunctionActionType,
  type CustomFunctionCompleteState,
  customFunctionReducer,
  type CustomFunctionStateAction,
} from './state';
import { CodeEditorTab } from './tabs/CodeEditorTab';
import { EnvironmentVariablesTab } from './tabs/EnvironmentVariablesTab';
import { TestingTab } from './tabs/TestingTab';
import type {
  CustomFunctionModel,
  TestRunHandler,
  UpdateCustomFunctionResult,
} from './types';
import { StyledModal } from './wrappers';

interface CustomFunctionInteriorProps {
  /** The custom function state */
  customFunctionState: CustomFunctionCompleteState;
  /** The dispatch function */
  dispatch: React.Dispatch<CustomFunctionStateAction>;
  /** The handler for running a test */
  handleTestRun: TestRunHandler;
}

const CustomFunctionInterior: React.FC<CustomFunctionInteriorProps> = ({
  customFunctionState,
  dispatch,
  handleTestRun,
}) => {
  const { code, envVars, testJson, testRun } = customFunctionState;

  // Get a clean set of the environment variable names
  const envVarNames = useMemo(
    () => filterEmptyEnvVars(envVars).map((envVar) => envVar.name),
    [envVars],
  );

  // When an environment variable name is updated, then the test JSON should update too
  useEffect(() => {
    try {
      const { environment, ...rest } = JSON.parse(testJson);
      // Get a clean set of the new environment variables
      const fullNewEnvironment = formatJsonEnvironmentObject(
        filterEmptyEnvVars(envVars),
      );
      // We're only going to overwrite the changed environment variables
      const newEnvironment: Record<string, string> = {};
      for (const envVarName of Object.keys(fullNewEnvironment)) {
        if (!(envVarName in environment)) {
          // If this is a new name, reset it to the default template string value value
          newEnvironment[envVarName] = fullNewEnvironment[envVarName];
        } else {
          // If this is an existing env var name, leave it as-is
          newEnvironment[envVarName] = environment[envVarName];
        }
      }
      const newValue = {
        environment: newEnvironment,
        ...rest,
      };
      dispatch({
        type: CustomFunctionActionType.UPDATE_TEST_JSON,
        testJson: JSON.stringify(newValue, null, 2),
      });
    } catch (error) {
      // If the test JSON is invalid, then we should not update it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envVarNames]);

  const pathArgs = useParams();

  const { filledTabs, filledActivePath, activeTabContents } = useTabBarTabs({
    useQueryParams: true,
    pathArgs,
    tabs: [
      {
        path: '',
        contents: (
          <CodeEditorTab
            value={code}
            onChange={(newCode) =>
              dispatch({
                type: CustomFunctionActionType.UPDATE_CODE,
                code: newCode,
              })
            }
            envVars={filterEmptyEnvVars(envVars)}
          />
        ),
        title: customFunctionMessages.tabCodeEditor,
      },
      {
        path: 'environment-variables',
        contents: (
          <EnvironmentVariablesTab value={envVars} dispatch={dispatch} />
        ),
        title: customFunctionMessages.tabEnvironmentVariables,
      },
      {
        path: 'testing',
        contents: (
          <TestingTab
            value={testJson}
            dispatch={dispatch}
            envVars={filterEmptyEnvVars(envVars)}
            testRun={testRun}
            handleTestRun={handleTestRun}
          />
        ),
        title: customFunctionMessages.tabTesting,
      },
    ],
    defaultPath: '',
  });

  return (
    <FlexColumn style={{ flexGrow: 1 }}>
      <TabBar tabs={filledTabs} activePath={filledActivePath} />
      <div
        style={{
          flexGrow: 1,
          height: 0,
        }}
      >
        <FlexColumn
          style={{
            height: '100%',
            gap: StyleUtils.Spacing.md,
            paddingTop: StyleUtils.Spacing.sm,
          }}
        >
          {activeTabContents}
        </FlexColumn>
      </div>
    </FlexColumn>
  );
};

export interface CustomFunctionProps {
  /** The custom function from GraphQL. If undefined, then the custom function is being created. */
  customFunction?: CustomFunctionModel;
  /** Whether the customFunction is loading, and the modal trigger should be disabled */
  loading?: boolean;
  /** The onClick handler for the save button */
  handleSave: (
    state: CustomFunctionCompleteState,
  ) => Promise<UpdateCustomFunctionResult>;
  /** The handler for running a test */
  handleTestRun: TestRunHandler;
  /** Whether the modal should be visible */
  modalVisible?: boolean;
  /** Whether the modal should be visible */
  setModalVisible?: (b: boolean) => void;
}

export const CustomFunction: React.FC<CustomFunctionProps> = ({
  customFunction: customFunctionModel,
  loading,
  handleSave,
  handleTestRun,
  modalVisible,
  setModalVisible,
}) => {
  const [innerVisible, setInnerVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { formatMessage } = useIntl();

  const [customFunctionState, dispatch] = useReducer<
    (
      state: CustomFunctionCompleteState,
      action: CustomFunctionStateAction,
    ) => CustomFunctionCompleteState
  >(customFunctionReducer, getHydratedCustomFunctionState(customFunctionModel));

  useEffect(() => {
    if (customFunctionModel) {
      dispatch({
        type: CustomFunctionActionType.HYDRATE_CUSTOM_FUNCTION,
        customFunctionModel,
      });
    }
  }, [customFunctionModel]);

  const setVisible = (visible: boolean): void =>
    setModalVisible ? setModalVisible(visible) : setInnerVisible(visible);
  const isVisible = modalVisible ?? innerVisible;

  // Save click handler
  const handleSaveOuter = async (
    customFunctionState: CustomFunctionCompleteState,
  ): Promise<void> => {
    setIsSaving(true);
    await handleSave(customFunctionState);
    setIsSaving(false);
  };

  // Save hotkey
  useHotkeys(
    ['ctrl+s', 'meta+s'],
    (keydownEvent) => {
      if (isVisible) {
        keydownEvent.stopPropagation();
        keydownEvent.preventDefault();
        handleSaveOuter(customFunctionState);
      }
    },
    {
      enableOnFormTags: true,
    },
  );

  return (
    <>
      <Button
        onClick={() => {
          if (!customFunctionModel) {
            // Save the custom function on modal open if it's being created for the first time
            handleSaveOuter(customFunctionState);
          }
          setVisible(true);
        }}
        disabled={loading}
      >
        {loading
          ? formatMessage(customFunctionMessages.titleLoading)
          : customFunctionModel
            ? formatMessage(customFunctionMessages.titleUpdate)
            : formatMessage(customFunctionMessages.titleCreate)}
      </Button>

      <StyledModal
        show={isVisible}
        onHide={() => setVisible(false)}
        header={formatMessage(customFunctionMessages.titleUpdate)}
        width="100%"
        closable={false}
        // Prevent the modal from being closed by clicking outside of it
        backdrop="static"
        // Prevent the modal from being closed by pressing the escape key
        keyboard={false}
        centered
        footer={
          <>
            <Button
              icon="clock-arrow-back"
              size="sm"
              variant="outline-secondary"
            >
              {formatMessage(customFunctionMessages.versionHistory)}
            </Button>
            <FlexRowCenterVertical
              style={{ gap: StyleUtils.Spacing.sm, justifyContent: 'end' }}
            >
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => setVisible(false)}
              >
                {formatMessage(customFunctionMessages.discardAll)}
              </Button>

              <Button
                size="sm"
                onClick={() => handleSaveOuter(customFunctionState)}
                loading={isSaving}
              >
                {formatMessage(customFunctionMessages.saveAll)}
              </Button>
            </FlexRowCenterVertical>
          </>
        }
      >
        <CustomFunctionInterior
          customFunctionState={customFunctionState}
          dispatch={dispatch}
          handleTestRun={handleTestRun}
        />
      </StyledModal>
    </>
  );
};
